import { type FunctionComponent, type PropsWithChildren, type ReactNode, useState } from 'react';
import { formatPrice } from '../../../../../helpers/general-helper/general-helper';
import { Drawer } from '../../../../common-components/drawer/drawer.component';
import { DropdownMenu, DropdownMenuSelect } from '../../../../common-components/dropdown-menu/dropdown-menu.component';
import ChevronRightIcon from '../../../../svgs/untitled-ui/chevron-right.svg';

const FormattedSelectedOptionTitle: FunctionComponent<{ selectedName: string; selectedPrice?: number }> = ({
	selectedName,
	selectedPrice
}) => (
	<>
		{selectedPrice ? (
			<div className="flex mv0">
				<p className="mv0" data-automation={`product-options-selected-name-${selectedName}`}>
					{selectedName}
				</p>{' '}
				<p className="theme-success mv0">- {formatPrice(selectedPrice)}</p>
			</div>
		) : (
			selectedName
		)}
	</>
);

export type ProductOptionsWrapperProps = {
	selectedName: string;
	selectedPrice?: number;
	children: ReactNode[];
	groupName?: string;
};

export const ProductOptionsDrawer: FunctionComponent<ProductOptionsWrapperProps> = ({
	groupName,
	selectedName,
	selectedPrice,
	children
}) => {
	const [isOpen, setIsOpen] = useState(false);
	const titleValue = `Select ${groupName}`;
	const drawerTitle: JSX.Element = <div className="flex flex-row flex-nowrap items-center justify-start w-100 f4 pr4">{titleValue}</div>;

	const toggle = () => setIsOpen(!isOpen);

	return (
		<div
			className="ba br2 theme-luxury:border-none! pointer relative"
			data-testid="product-options-drawer"
			data-automation={`product-options-drawer-${groupName}`}>
			<DropdownMenuSelect toggle={toggle} expanded={isOpen}>
				<FormattedSelectedOptionTitle selectedName={selectedName} selectedPrice={selectedPrice} />
				<ChevronRightIcon className="flex-end ml2 f3 w2" />
			</DropdownMenuSelect>
			{isOpen && (
				<Drawer title={drawerTitle} ariaLabel={titleValue} direction="right" showOverlay={false}>
					{children}
				</Drawer>
			)}
		</div>
	);
};

export const ProductOptionsDropdown: FunctionComponent<
	ProductOptionsWrapperProps & { fullWidth?: boolean; shouldCloseOnChoice?: boolean }
> = ({ selectedName, selectedPrice, children, fullWidth = false, shouldCloseOnChoice = true, groupName }) => (
	<div
		data-testid="product-options-dropdown"
		data-automation={`product-options-dropdown-${groupName}`}
		style={{ width: fullWidth ? '100%' : '18rem' }}>
		<DropdownMenu
			title={<FormattedSelectedOptionTitle selectedName={selectedName} selectedPrice={selectedPrice} />}
			shouldCloseOnChoice={shouldCloseOnChoice}>
			{children}
		</DropdownMenu>
	</div>
);

export const ProductOptionsGrid: FunctionComponent<PropsWithChildren & { groupName?: string }> = ({ children, groupName }) => (
	<div
		className="flex flex-row flex-wrap items-start mt2 w-100"
		data-testid="product-options-grid"
		data-automation={`product-options-grid-${groupName}`}
		style={{ gap: '.5rem' }}>
		{children}
	</div>
);
